import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import {
	IAdminDatasetPermission,
	removeAdminDatasetPermission,
	useAdminUserDatasetPermissionsQuery,
} from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';
import AddDatasetPermissionDialog from './AddDatasetPermissionDialog';
import CopyDatasetPermissionsDialog from './CopyDatasetPermissionsDialog';
import EditCompanySubsetDialog from './EditCompanySubsetDialog';

interface Props {
	userId: number;
	userEmail: string;
	/** Fill the parent (a flex column) instead of using a fixed viewport-based height. */
	fill?: boolean;
}

interface RemoveTarget {
	permission: IAdminDatasetPermission;
}

export default function UserDatasetPermissionsTable({ userId, userEmail, fill }: Props) {
	const queryClient = useQueryClient();
	const { data: permissions, isLoading } = useAdminUserDatasetPermissionsQuery(userId);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [copyDialogOpen, setCopyDialogOpen] = useState(false);
	const [subsetTarget, setSubsetTarget] = useState<IAdminDatasetPermission | null>(null);
	const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
	const [removing, setRemoving] = useState(false);
	const [removeError, setRemoveError] = useState<string | null>(null);

	async function handleRemoveConfirm() {
		if (!removeTarget) return;
		setRemoving(true);
		setRemoveError(null);
		try {
			await removeAdminDatasetPermission(userId, removeTarget.permission.id);
			await queryClient.invalidateQueries(['admin-user-dataset-permissions', userId]);
			setRemoveTarget(null);
		} catch (e: any) {
			setRemoveError(e?.response?.data?.detail ?? 'Failed to remove permission.');
		} finally {
			setRemoving(false);
		}
	}

	const columns: GridColDef[] = [
		{
			field: 'vendor_name',
			headerName: 'Vendor',
			flex: 1,
			minWidth: 140,
		},
		{
			field: 'dataset_name',
			headerName: 'Dataset',
			flex: 1.5,
			minWidth: 180,
		},
		{
			field: 'internal_description',
			headerName: 'Internal Description',
			flex: 2,
			minWidth: 200,
		},
		{
			field: 'is_platform_included',
			headerName: 'Platform Included',
			width: 130,
			type: 'boolean',
		},
		{
			field: 'access_from',
			headerName: 'Access From',
			width: 130,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'updated_at',
			headerName: 'Last Updated',
			width: 130,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'ticker_subset',
			headerName: 'Ticker Subset',
			flex: 2,
			minWidth: 180,
		},
		{
			field: 'actions',
			headerName: '',
			width: 88,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams) => (
				<>
					<Tooltip title="Edit company subset">
						<IconButton
							size="small"
							onClick={() => setSubsetTarget(params.row as IAdminDatasetPermission)}
						>
							<EditIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Remove permission">
						<IconButton
							size="small"
							color="error"
							onClick={() => setRemoveTarget({ permission: params.row as IAdminDatasetPermission })}
						>
							<DeleteOutlineIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</>
			),
		},
	];

	return (
		<>
			<Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1 }}>
				<Button
					size="small"
					variant="outlined"
					startIcon={<ContentCopyIcon />}
					onClick={() => setCopyDialogOpen(true)}
				>
					Copy Permissions
				</Button>
				<Button
					size="small"
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => setDialogOpen(true)}
				>
					Add Dataset
				</Button>
			</Stack>
			<Box sx={fill ? { flex: 1, minHeight: 0, width: '100%' } : { height: 'calc(100vh - 350px)', width: '100%' }}>
				<DataGrid
					rows={permissions ?? []}
					columns={columns}
					loading={isLoading}
					density="compact"
					disableSelectionOnClick
					components={{ Toolbar: AdminQuickFilterToolbar }}
					componentsProps={{ toolbar: { showQuickFilter: true } }}
				/>
			</Box>
			<AddDatasetPermissionDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				userId={userId}
				userEmail={userEmail}
			/>
			<CopyDatasetPermissionsDialog
				open={copyDialogOpen}
				onClose={() => setCopyDialogOpen(false)}
				userId={userId}
				userEmail={userEmail}
			/>
			<EditCompanySubsetDialog
				open={!!subsetTarget}
				onClose={() => setSubsetTarget(null)}
				userId={userId}
				userEmail={userEmail}
				permission={subsetTarget}
			/>

			{/* Remove confirmation dialog */}
			<Dialog
				open={!!removeTarget}
				onClose={() => !removing && setRemoveTarget(null)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { backgroundImage: 'none' } }}
			>
				<DialogTitle>Remove Dataset Permission</DialogTitle>
				<DialogContent>
					{removeError && (
						<Typography color="error" variant="body2" sx={{ mb: 1 }}>
							{removeError}
						</Typography>
					)}
					<Typography>
						Remove access to{' '}
						<strong>
							{removeTarget?.permission.vendor_name} —{' '}
							{removeTarget?.permission.dataset_name}
						</strong>{' '}
						for <strong>{userEmail}</strong>?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRemoveTarget(null)} disabled={removing}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleRemoveConfirm}
						disabled={removing}
						startIcon={removing ? <CircularProgress size={16} /> : undefined}
					>
						{removing ? 'Removing…' : 'Remove'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
